import React from 'react';
import PropTypes from 'prop-types';

import classNames from 'classnames';
import autobind from 'class-autobind';

import map from 'lodash/map';
import forEach from 'lodash/forEach';
import filter from 'lodash/filter';
import isEmpty from 'lodash/isEmpty';

export const UNLIMITED_OPEN_ITEMS_LIMIT = 0;
export const DEFAULT_OPEN_ITEMS_LIMIT = UNLIMITED_OPEN_ITEMS_LIMIT;

class BaseAccordion extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      openItemKeys: props.openIndexOverrides || props.defaultOpenItemKeys || []
    };

    autobind(this);
  }

  componentDidUpdate(prevProps) {
    // what on earth?
    //
    // this is a bit of a hack to work around the fact that this state is
    // localized to this component and there are instances (like in-site
    // search) where we would prefer a global state object instead.
    //
    // by supplying overrides for the open accordion folds we can effectively
    // control what is open and closed outside of this component itself. this
    // works very well for instances where you want to find results in an
    // accordion and open the pane to the matched string.
    //
    // we are careful to clean up after ourselves by resettng this override
    // after it has been applied to local state. this configuration mostly acts
    // as a trigger to force a local state change once and then return control
    // of that state to this local component.
    if (prevProps.openIndexOverrides !== this.props.openIndexOverrides) {
      if (!isEmpty(this.props.openIndexOverrides)) {
        this.setState({
          openItemKeys: this.props.openIndexOverrides
        }, ()=> {
          this.props.onResetOpenIndexOverrides();
        });
      }
    }
  }

  handleOpenItem(itemKey){
    let openItemKeys = [...this.state.openItemKeys, itemKey];

    const maxOpenItemsLimit = this.props.maxOpenItemsLimit ? 
    this.props.maxOpenItemsLimit < 0 ? UNLIMITED_OPEN_ITEMS_LIMIT : this.props.maxOpenItemsLimit
    :
      DEFAULT_OPEN_ITEMS_LIMIT
    ;
    
    const excessItemCount = openItemKeys.length - maxOpenItemsLimit;
    
    if (maxOpenItemsLimit !== UNLIMITED_OPEN_ITEMS_LIMIT && excessItemCount > 0){
      openItemKeys = openItemKeys.slice(excessItemCount);
    }
    
    this.setState({ openItemKeys });
  }

  handleCloseItem(itemKey){
    let openItemKeys = [...this.state.openItemKeys];
    
    openItemKeys = filter(openItemKeys, (key) => key !== itemKey);

    this.setState({ openItemKeys });
  }

  render(){
    const { 
      className,
      items,
      renderItem,
      itemKeyFunc
    } = this.props;

    const {
      openItemKeys
    } = this.state;

    const isOpenByKey = {};
    forEach(openItemKeys, (key) => (isOpenByKey[key] = true));

    return (
      <div className={classNames('accordion', className)}>
        { map(items, (item, index) => {
          const itemKey = itemKeyFunc ? itemKeyFunc(item) : index;

          return (
            <AccordionItem
              key={itemKey}
              itemIndex={index}
              itemKey={itemKey}
              item={item}
              isOpen={!!isOpenByKey[itemKey]}
              renderItem={renderItem}
              onOpenItem={this.handleOpenItem}
              onCloseItem={this.handleCloseItem}
            />
          );
        })}
      </div>
    );
  }
}

BaseAccordion.propTypes = {
  maxOpenItemsLimit: PropTypes.number,
  defaultOpenItemKeys: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]).isRequired),
  className: PropTypes.string,
  renderItem: PropTypes.func,
  items: PropTypes.array.isRequired
};

export default BaseAccordion;

const AccordionItem = ({ 
  item, 
  itemIndex,
  itemKey,
  isOpen, 
  renderItem, 
  onCloseItem, 
  onOpenItem 
}) => (
  renderItem({ 
    item, 
    itemIndex, 
    itemKey,
    isOpen,
    onCloseItem: () => onCloseItem(itemKey),
    onOpenItem: () => onOpenItem(itemKey),
    onToggleItem: () => isOpen ? onCloseItem(itemKey) : onOpenItem(itemKey)
  })
);
